/**
 * PSP/CRE Tool v2.1 - Evacuation Asset Allocation Factors
 * 
 * Defines evacuation asset allocation by unit type.
 * Different unit types require different evacuation asset mixes based on:
 * - Mobility characteristics (wheeled vs tracked)
 * - Operating terrain (roads, off-road, complex)
 * - Tactical doctrine (heavy vs light)
 * - Casualty patterns (intensity, distribution)
 * 
 * @fileoverview Evacuation asset factors by unit type
 * @module constants/evacuationAssetFactors
 * @author Dr. Tatar Radu MD
 * @date March 9, 2026
 * @version 2.3 - ESLint fix (March 13, 2026): TACTICAL_ICONS removed from import — defined but never used (line 48 warning in production build).
 * 
 * DOCTRINAL NOTE:
 * MEDEVAC helicopters are NOT organic to maneuver brigades.
 * Helicopter allocation is handled in Tab 3 from pools.
 * 
 * VERSION 2.2 CHANGES (March 9, 2026 - IMP-007):
 * - Add MOUNTAIN, STRYKER, AVIATION, SUSTAINMENT, MEDICAL to EVACUATION_ASSET_FACTORS
 * - Add UNIT_TYPE_TO_EVAC_KEY adapter map (ARMOR->ARMORED bridge + new types)
 * - Add getEvacuationFactorForBrigade() — throws on unknown (calculator use)
 * - Fix getEvacuationFactors() — apply adapter map before lookup
 * - Fix calculateEvacuationAssets() — replace console.error with throw (APP_RULES 6.1)
 * - Fix all direct emoji in entries and helpers (APP_RULES 5.1)
 * - Fix ICONS block — INFANTRY/ARMOR now from MILITARY_ICONS (APP_RULES 5.1)
 * - Fix NATO terminology in JSDoc comments (area/direct support)
 * - SPECIAL_FORCES retained: SF Corps emerging (Poland MNC-NE, Romania SF Brigade)
 *
 * VERSION 2.1 CHANGES:
 * - icons.js integration (TRANSPORT_ICONS, STATUS_ICONS)
 * - Added getUnitTypeOptions() with full metadata for UI
 * - Added getEchelonOptions() for base allocations
 * - Added validateEvacuationConfig() function
 * - Enhanced educational metadata
 * - Added Ukraine 2022-2024 lessons
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  TRANSPORT_ICONS,
  MILITARY_ICONS,
  TERRAIN_ICONS,
  MEDICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN:  STATUS_ICONS?.GREEN  || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED:    STATUS_ICONS?.RED    || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',

  // Transport icons
  AMBULANCE:  TRANSPORT_ICONS?.AMBULANCE  || '🚑',
  HELICOPTER: TRANSPORT_ICONS?.HELICOPTER || '🚁',
  TANK:       TRANSPORT_ICONS?.TANK       || '🛡️',

  // Tactical icons
  WARNING: STATUS_ICONS?.WARNING  || '⚠️',

  // Unit type icons — sourced from MILITARY_ICONS (APP_RULES 5.1)
  // Fallback chain: MILITARY_ICONS -> TRANSPORT_ICONS -> TACTICAL_ICONS
  INFANTRY:      MILITARY_ICONS?.SOLDIER       || TRANSPORT_ICONS?.AMBULANCE,
  ARMOR:         MILITARY_ICONS?.TANK          || TRANSPORT_ICONS?.TANK,
  AIRBORNE:      TRANSPORT_ICONS?.AIRPLANE     || STATUS_ICONS?.WARNING,
  SPECIAL_FORCES:MILITARY_ICONS?.TARGET        || STATUS_ICONS?.WARNING,
  RECONNAISSANCE:MILITARY_ICONS?.BINOCULARS    || TRANSPORT_ICONS?.TANK,
  ARTILLERY:     MILITARY_ICONS?.ARTILLERY     || STATUS_ICONS?.WARNING,
  LOGISTICS_UNIT:TRANSPORT_ICONS?.TRUCK         || TRANSPORT_ICONS?.AMBULANCE,
  MOUNTAIN:      TERRAIN_ICONS?.MOUNTAIN       || TRANSPORT_ICONS?.AMBULANCE,
  STRYKER:       TRANSPORT_ICONS?.ARMORED      || TRANSPORT_ICONS?.AMBULANCE,
  AVIATION:      TRANSPORT_ICONS?.HELICOPTER   || TRANSPORT_ICONS?.AIRPLANE,
  MEDICAL_UNIT:  TRANSPORT_ICONS?.AMBULANCE,

  // Capability display icons — for factor summary tables (APP_RULES 5.1)
  CAP_WHEELED:    TRANSPORT_ICONS?.AMBULANCE  || '🚑',
  CAP_TRACKED:    TRANSPORT_ICONS?.TANK       || '🛡️',
  CAP_HELICOPTER: TRANSPORT_ICONS?.HELICOPTER || '🚁',
  CAP_STRETCHER:  MEDICAL_ICONS?.STRETCHER         || TRANSPORT_ICONS?.AMBULANCE
};

// ============================================================================
// BASE EVACUATION ALLOCATIONS BY ECHELON
// ============================================================================

export const BASE_EVACUATION_ALLOCATIONS = {
  BATTALION: {
    echelon: 'BATTALION',
    icon: ICONS.GREEN,
    wheeled_ambulances: 4,
    tracked_ambulances: 4,
    helicopters: 0,
    stretcher_teams: 8,
    notes: 'No organic helicopters at battalion level',
    educational: {
      key_point: 'Smallest organic medical unit',
      teaching_note: 'Battalion Aid Station (BAS) focus'
    }
  },
  
  BRIGADE: {
    echelon: 'BRIGADE',
    icon: ICONS.YELLOW,
    wheeled_ambulances: 12,
    tracked_ambulances: 10,
    helicopters: 0,
    stretcher_teams: 24,
    notes: 'MEDEVAC helicopters requested from higher echelon (Tab 3)',
    educational: {
      key_point: 'Baseline for mechanized infantry brigade',
      teaching_note: 'Helicopter support from Division CAB or Air Force'
    }
  },
  
  DIVISION: {
    echelon: 'DIVISION',
    icon: ICONS.ORANGE,
    wheeled_ambulances: 24,
    tracked_ambulances: 18,
    helicopters: 0,
    stretcher_teams: 40,
    notes: 'Helicopter allocation varies by nation - handled in Tab 3',
    educational: {
      key_point: 'US has 15 HH-60M in organic CAB',
      teaching_note: 'Romanian: Request from Air Force'
    }
  },
  
  CORPS: {
    echelon: 'CORPS',
    icon: ICONS.RED,
    wheeled_ambulances: 40,
    tracked_ambulances: 30,
    helicopters: 0,
    stretcher_teams: 60,
    notes: 'Corps-level MEDEVAC assets are theater resources',
    educational: {
      key_point: 'Theater-level medical assets',
      teaching_note: 'Role 3 CSH support element'
    }
  }
};

// ============================================================================
// UNIT TYPE EVACUATION ASSET FACTORS
// ============================================================================

export const EVACUATION_ASSET_FACTORS = {
  HEAVY: {
    unit_type: 'HEAVY',
    label: 'Heavy (Armored/Tanks)',
    description: 'Heavy armored formations (tanks, heavy IFV)',
    icon: ICONS.ARMOR,
    color: '#795548',
    
    wheeled_factor: 0.6,
    tracked_factor: 1.8,
    helicopter_factor: 1.4,
    stretcher_factor: 0.6,
    
    rationale: 'Heavy units need tracked ambulances to maintain mobility off-road. Higher casualty rates from high-intensity combat require more helicopters.',
    tactical_notes: 'Heavy formations operate primarily off-road. Wheeled ambulances cannot keep pace with tracked vehicles.',
    
    educational: {
      key_point: 'Tracked ambulances essential for forward evacuation',
      teaching_note: 'M113/AMPV ambulance keeps pace with tanks',
      ukraine_lesson: 'BTR-based ambulances lost to same threats as combat vehicles'
    }
  },
  
  INFANTRY: {
    unit_type: 'INFANTRY',
    label: 'Infantry (Light)',
    description: 'Light infantry, foot-mobile forces',
    icon: ICONS.INFANTRY,
    color: '#4CAF50',
    
    wheeled_factor: 1.3,
    tracked_factor: 0.4,
    helicopter_factor: 0.9,
    stretcher_factor: 2.0,
    
    rationale: 'Light infantry operates primarily on foot. Heavy reliance on manual stretcher carry from point of injury.',
    tactical_notes: 'Light infantry casualty evacuation is labor-intensive. Stretcher teams critical for terrain where vehicles cannot operate.',
    
    educational: {
      key_point: '2x stretcher teams - manual carry essential',
      teaching_note: 'Urban/complex terrain limits vehicle access',
      ukraine_lesson: 'Ukrainian infantry relies heavily on manual evacuation'
    }
  },
  
  MECHANIZED: {
    unit_type: 'MECHANIZED',
    label: 'Mechanized Infantry',
    description: 'Mechanized infantry in IFV/APC',
    icon: ICONS.AMBULANCE,
    color: '#2196F3',
    
    wheeled_factor: 1.0,
    tracked_factor: 1.0,
    helicopter_factor: 1.0,
    stretcher_factor: 1.0,
    
    rationale: 'Mechanized infantry is the baseline for all evacuation asset allocations. Balanced mix of evacuation capabilities.',
    tactical_notes: 'Mechanized forces operate in mixed terrain. This is the reference standard.',
    
    educational: {
      key_point: 'Baseline (1.0x) for all factors',
      teaching_note: 'Compare other unit types to this standard'
    }
  },
  
  ARMOR: {
    unit_type: 'ARMOR',
    label: 'Armor',
    description: 'Tank units, armored cavalry',
    icon: ICONS.ARMOR,
    color: '#607D8B',
    
    wheeled_factor: 0.5,
    tracked_factor: 2.0,
    helicopter_factor: 1.5,
    stretcher_factor: 0.5,
    
    rationale: 'Armored units operate exclusively with tracked vehicles. Maximum tracked ambulance allocation required.',
    tactical_notes: 'Armored units in cross-country assault cannot use wheeled vehicles. Helicopter evacuation critical for speed.',
    
    educational: {
      key_point: '2x tracked ambulances required',
      teaching_note: 'AMPV MEV designed for ABCT support',
      ukraine_lesson: 'Tank crews often evacuated by other tanks, not ambulances'
    }
  },
  
  AIRBORNE: {
    unit_type: 'AIRBORNE',
    label: 'Airborne',
    description: 'Parachute infantry units',
    icon: ICONS.AIRBORNE,
    color: '#9C27B0',
    
    wheeled_factor: 0.8,
    tracked_factor: 0.3,
    helicopter_factor: 2.5,
    stretcher_factor: 1.5,
    
    rationale: 'Airborne units initially have no organic vehicles. Helicopter evacuation critical until ground linkup.',
    tactical_notes: 'Post-drop, airborne relies on helicopter evacuation until surface LOC established.',
    
    educational: {
      key_point: '2.5x helicopter priority until linkup',
      teaching_note: 'Golden hour often impossible without helicopter',
      ukraine_lesson: 'Air assault operations limited by AD threat'
    }
  },
  
  AIR_ASSAULT: {
    unit_type: 'AIR_ASSAULT',
    label: 'Air Assault',
    description: 'Helicopter-inserted infantry',
    icon: ICONS.HELICOPTER,
    color: '#673AB7',
    
    wheeled_factor: 0.6,
    tracked_factor: 0.2,
    helicopter_factor: 3.0,
    stretcher_factor: 1.3,
    
    rationale: 'Air assault units are helicopter-dependent for all operations. Maximum helicopter evacuation priority.',
    tactical_notes: 'MEDEVAC helicopters often accompany air assault missions. Organic to mission planning.',
    
    educational: {
      key_point: '3x helicopter priority - highest of all types',
      teaching_note: 'MEDEVAC bird in lift package is standard',
      ukraine_lesson: 'Air assault severely restricted by AD environment'
    }
  },
  
  SPECIAL_FORCES: {
    unit_type: 'SPECIAL_FORCES',
    label: 'Special Operations',
    description: 'Special operations units',
    icon: ICONS.SPECIAL_FORCES,
    color: '#000000',
    
    wheeled_factor: 0.4,
    tracked_factor: 0.2,
    helicopter_factor: 2.0,
    stretcher_factor: 1.2,
    
    rationale: 'Special Forces operate independently. Minimal organic vehicles. Priority helicopter evacuation required.',
    tactical_notes: 'Teams trained for self-aid and buddy-aid. Helicopter extraction standard.',
    
    educational: {
      key_point: 'Self-sufficient small teams',
      teaching_note: 'Enhanced TCCC training reduces stretcher needs'
    }
  },
  
  RECONNAISSANCE: {
    unit_type: 'RECONNAISSANCE',
    label: 'Reconnaissance',
    description: 'Reconnaissance units (the cavalry squadron of the US, the reconnaissance company or regiment of other nations)',
    icon: ICONS.RECONNAISSANCE,
    color: '#FF9800',
    
    wheeled_factor: 1.2,
    tracked_factor: 1.2,
    helicopter_factor: 1.3,
    stretcher_factor: 0.8,
    
    rationale: 'Cavalry units require highly mobile medical support. Mix of wheeled and tracked for flexibility.',
    tactical_notes: 'Cavalry operates independently forward. Medical support must maintain pace.',
    
    educational: {
      key_point: 'High mobility, flexible mix',
      teaching_note: 'Scouts need responsive evacuation'
    }
  },
  
  ARTILLERY: {
    unit_type: 'ARTILLERY',
    label: 'Artillery',
    description: 'Field artillery units',
    icon: ICONS.ARTILLERY,
    color: '#795548',
    
    wheeled_factor: 1.4,
    tracked_factor: 0.6,
    helicopter_factor: 0.7,
    stretcher_factor: 1.1,
    
    rationale: 'Artillery operates from fixed position areas with road access. Wheeled ambulances sufficient.',
    tactical_notes: 'Counter-battery fire primary casualty producer. Modern artillery increasingly targeted.',
    
    educational: {
      key_point: 'Position areas accessible by road',
      teaching_note: 'Lower helicopter priority than maneuver units',
      ukraine_lesson: 'Artillery taking heavy casualties from counter-battery and drones'
    }
  },
  
  LOGISTICS: {
    unit_type: 'LOGISTICS',
    label: 'Logistics/Support',
    description: 'Combat service support units',
    icon: ICONS.LOGISTICS_UNIT,
    color: '#9E9E9E',
    
    wheeled_factor: 1.6,
    tracked_factor: 0.2,
    helicopter_factor: 0.5,
    stretcher_factor: 0.9,
    
    rationale: 'Logistics units operate in rear areas with good road networks. Lowest helicopter priority.',
    tactical_notes: 'Support units medical planning emphasizes routine care. Modern warfare sees increasing rear area attacks.',
    
    educational: {
      key_point: 'Rear area - primarily wheeled',
      teaching_note: 'Lowest priority in medical regulation',
      ukraine_lesson: 'Logistics convoys increasingly targeted by drones'
    }
  },

  MOUNTAIN: {
    unit_type: 'MOUNTAIN',
    label: 'Mountain',
    description: 'Mountain warfare specialists — Vanatatori de Munte, Gebirgsjager, Alpini',
    icon: ICONS.MOUNTAIN,
    color: '#795548',

    wheeled_factor: 1.0,
    tracked_factor: 0.3,
    helicopter_factor: 1.8,
    stretcher_factor: 2.5,

    rationale: 'Mountain terrain limits vehicles to valley roads. Tracked vehicles largely unusable on steep slopes. Helicopter is primary evacuation means; stretcher carry dominant for all forward casualty movement.',
    tactical_notes: 'Mule/porter teams substitute for wheeled ambulances above the snowline. Golden hour compliance is critically dependent on helicopter availability. Romanian Vanatatori de Munte, German Gebirgsjager, Italian Alpini all use identical doctrine.',

    educational: {
      key_point: '1.8x helicopter priority + 2.5x stretcher — terrain dominates',
      teaching_note: 'Mule teams and stretcher bearers primary above vehicle limit line',
      ukraine_lesson: 'Mountain/hill terrain forces manual evacuation even in modern warfare'
    }
  },

  STRYKER: {
    unit_type: 'STRYKER',
    label: 'Stryker (SBCT)',
    description: 'US Army Stryker Brigade Combat Team — wheeled medium force',
    icon: ICONS.STRYKER,
    color: '#2196F3',

    wheeled_factor: 1.5,
    tracked_factor: 0.3,
    helicopter_factor: 1.0,
    stretcher_factor: 0.9,

    rationale: 'SBCT is an all-wheeled formation. Primary MEV is M1133 (Stryker chassis). No organic tracked ambulances. Helicopter demand at baseline — SBCT operates in semi-complex terrain with road access.',
    tactical_notes: 'M1133 MEV on wheeled Stryker chassis is the organic platform. Wheeled ambulances dominant throughout. nationalStandards.js notes STRYKER maps to MECHANIZED for casualty rates; evacuation asset mix differs significantly.',

    educational: {
      key_point: '1.5x wheeled — all-wheeled formation, no tracked ambulances',
      teaching_note: 'M1133 MEV is the organic SBCT ambulance',
      ukraine_lesson: 'Wheeled vehicles vulnerable to mines and drones on roads'
    }
  },

  AVIATION: {
    unit_type: 'AVIATION',
    label: 'Aviation Brigade',
    description: 'Aviation support brigade — operates from airfields in rear areas',
    icon: ICONS.AVIATION,
    color: '#03A9F4',

    wheeled_factor: 1.3,
    tracked_factor: 0.2,
    helicopter_factor: 0.8,
    stretcher_factor: 0.7,

    rationale: 'Aviation brigades operate from established airfields with good road infrastructure. Wheeled ambulances primary. Helicopter factor below baseline — organic aircraft nearby but not configured for MEDEVAC. Low stretcher demand reflects rear-area positioning.',
    tactical_notes: 'Casualties concentrated at airfield (ground accidents, maintenance, sporadic shelling). Wheeled evacuation to R2B straightforward. MEDEVAC helicopter requests go through standard medical regulation chain.',

    educational: {
      key_point: 'Rear area — wheeled primary, low helicopter MEDEVAC demand',
      teaching_note: 'Organic aircraft are CASEVAC-capable but not dedicated MEDEVAC',
      ukraine_lesson: 'Aviation units subject to long-range strikes on airfields'
    }
  },


  MEDICAL: {
    unit_type: 'MEDICAL',
    label: 'Medical Brigade',
    description: 'Medical support brigade — Role 2/3 medical support formation',
    icon: ICONS.MEDICAL_UNIT,
    color: '#E53935',

    wheeled_factor: 1.2,
    tracked_factor: 0.4,
    helicopter_factor: 0.9,
    stretcher_factor: 1.1,

    rationale: 'Medical brigades require evacuation for their own casualties while managing higher patient throughput. Enhanced organic treatment capability reduces net helicopter MEDEVAC demand. Moderate tracked allocation for forward medical elements. Slightly elevated stretcher factor reflects patient handling within the medical system.',
    tactical_notes: 'Medical personnel are protected under Geneva Conventions but suffer casualties from proximity to combat areas and deliberate targeting. Own-force casualty rate lower than maneuver units (modifier 0.3 in UNIT_TYPES). Evacuation asset mix reflects rear-area positioning with some forward R2B elements.',

    educational: {
      key_point: 'Protected status — own casualty rate low, but patient throughput high',
      teaching_note: 'Medical units need evacuation for own casualties and patient overflow',
      ukraine_lesson: 'Medical facilities deliberately targeted — internal casualty management required'
    }
  },
  AIR_DEFENSE: {
    unit_type: 'AIR_DEFENSE',
    label: 'Air Defense',
    description: 'Air defense brigade',
    icon: ICONS.MEDICAL_UNIT,
    color: '#9E9E9E',

    wheeled_factor: 1.0,
    tracked_factor: 1.0,
    helicopter_factor: 1.0,
    stretcher_factor: 1.0,

    rationale: 'No data. These four brigade types had no row and the calculator used the baseline (1.0 for every factor) without saying so (it caught the error); the row makes that explicit (naming audit step 4, 2026-10-24). EDUCATED GUESS: to be sourced.',
    tactical_notes: 'Baseline factors carried from the silent default.',

    educational: {
      key_point: 'Baseline (1.0x) carried from the earlier silent default',
      teaching_note: 'No evacuation asset data for this brigade type yet'
    }
  },
  ENGINEERS: {
    unit_type: 'ENGINEERS',
    label: 'Engineers',
    description: 'Engineer brigade',
    icon: ICONS.MEDICAL_UNIT,
    color: '#9E9E9E',

    wheeled_factor: 1.0,
    tracked_factor: 1.0,
    helicopter_factor: 1.0,
    stretcher_factor: 1.0,

    rationale: 'No data. These four brigade types had no row and the calculator used the baseline (1.0 for every factor) without saying so (it caught the error); the row makes that explicit (naming audit step 4, 2026-10-24). EDUCATED GUESS: to be sourced.',
    tactical_notes: 'Baseline factors carried from the silent default.',

    educational: {
      key_point: 'Baseline (1.0x) carried from the earlier silent default',
      teaching_note: 'No evacuation asset data for this brigade type yet'
    }
  },
  HQ: {
    unit_type: 'HQ',
    label: 'Headquarters',
    description: 'Headquarters element',
    icon: ICONS.MEDICAL_UNIT,
    color: '#9E9E9E',

    wheeled_factor: 1.0,
    tracked_factor: 1.0,
    helicopter_factor: 1.0,
    stretcher_factor: 1.0,

    rationale: 'No data. These four brigade types had no row and the calculator used the baseline (1.0 for every factor) without saying so (it caught the error); the row makes that explicit (naming audit step 4, 2026-10-24). EDUCATED GUESS: to be sourced.',
    tactical_notes: 'Baseline factors carried from the silent default.',

    educational: {
      key_point: 'Baseline (1.0x) carried from the earlier silent default',
      teaching_note: 'No evacuation asset data for this brigade type yet'
    }
  },
  SIGNAL: {
    unit_type: 'SIGNAL',
    label: 'Signal',
    description: 'Signal brigade',
    icon: ICONS.MEDICAL_UNIT,
    color: '#9E9E9E',

    wheeled_factor: 1.0,
    tracked_factor: 1.0,
    helicopter_factor: 1.0,
    stretcher_factor: 1.0,

    rationale: 'No data. These four brigade types had no row and the calculator used the baseline (1.0 for every factor) without saying so (it caught the error); the row makes that explicit (naming audit step 4, 2026-10-24). EDUCATED GUESS: to be sourced.',
    tactical_notes: 'Baseline factors carried from the silent default.',

    educational: {
      key_point: 'Baseline (1.0x) carried from the earlier silent default',
      teaching_note: 'No evacuation asset data for this brigade type yet'
    }
  }
};

// ============================================================================
// IMP-007: UNIT TYPE TO EVACUATION KEY ADAPTER
// ============================================================================

/**
 * Maps brigade.unit_type values (from unitTypes.js + nationalStandards.js) to
 * EVACUATION_ASSET_FACTORS keys. Required because unitTypes.js uses 'ARMOR'
 * (2026-10-24: EVACUATION_ASSET_FACTORS used 'ARMORED' and 'CAVALRY'; both now use the unit type keys, the adapter is identity for them).
 *
 * All entries not listed here are identity mappings (key === value in EVACUATION_ASSET_FACTORS).
 *
 * Usage: casualtyCalculator.js computeCompositeEvacFactor() applies this adapter
 * before calling getEvacuationFactorForBrigade().
 *
 * Doctrinal basis: nationalStandards.js available_brigade_types[] defines all
 * legal brigade.unit_type values per nation.
 */
export const UNIT_TYPE_TO_EVAC_KEY = {
  // New entries added v2.2 — identity mappings (listed for clarity)
  MOUNTAIN:    'MOUNTAIN',
  STRYKER:     'STRYKER',
  AVIATION:    'AVIATION',
  MEDICAL:     'MEDICAL',

  // SPECIAL_FORCES: identity — retained for emerging SF Corps formations
  // (Poland MNC-NE Special Forces component, Romania Special Forces Brigade)
  SPECIAL_FORCES: 'SPECIAL_FORCES',

  // 2026-10-24 (naming audit step 3): ARMOR and RECONNAISSANCE are identity now (the table used ARMORED and CAVALRY)
  RECONNAISSANCE: 'RECONNAISSANCE'
};

// ============================================================================
// UKRAINE 2022-2024 LESSONS
// ============================================================================

export const UKRAINE_EVACUATION_LESSONS = {
  applicable: true,
  source: 'Ukraine 2022-2024 combat data',
  icon: ICONS.WARNING,
  
  key_findings: [
    'Tracked ambulances take same casualties as combat vehicles',
    'Wheeled ambulances vulnerable on roads to mines/drones',
    'Civilian vehicles used extensively for evacuation',
    'Motorcycle/ATV evacuation from trenches common'
  ],
  
  factor_adjustments: {
    all_tracked_multiplier: 0.8,
    all_wheeled_multiplier: 0.9,
    stretcher_multiplier: 1.3,
    reason: 'Vehicle losses require more manual evacuation'
  },
  
  recommendations: [
    'Plan for higher vehicle attrition',
    'Train on improvised evacuation methods',
    'Civilian vehicle integration essential',
    'Night movement for all ground evacuation'
  ]
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get unit type options for UI dropdown
 * @returns {Array} Unit type options
 */
export const getUnitTypeOptions = () => {
  return Object.entries(EVACUATION_ASSET_FACTORS).map(([key, factor]) => ({
    value: factor.unit_type,
    label: factor.label,
    icon: factor.icon,
    color: factor.color,
    description: factor.description,
    wheeledFactor: factor.wheeled_factor,
    trackedFactor: factor.tracked_factor,
    helicopterFactor: factor.helicopter_factor,
    stretcherFactor: factor.stretcher_factor
  }));
};

/**
 * Get echelon options for UI dropdown
 * @returns {Array} Echelon options
 */
export const getEchelonOptions = () => {
  return Object.entries(BASE_EVACUATION_ALLOCATIONS).map(([key, alloc]) => ({
    value: key,
    label: key,
    icon: alloc.icon,
    wheeledAmbulances: alloc.wheeled_ambulances,
    trackedAmbulances: alloc.tracked_ambulances,
    stretcherTeams: alloc.stretcher_teams
  }));
};

/**
 * Get factors summary for display
 * @param {string} unitType - Unit type
 * @returns {Object} Factors summary with icons
 */
export const getFactorsSummary = (unitType) => {
  const factors = EVACUATION_ASSET_FACTORS[unitType];
  if (!factors) return null;
  
  return {
    unitType: factors.unit_type,
    label: factors.label,
    icon: factors.icon,
    factors: [
      { type: 'Wheeled',    factor: factors.wheeled_factor,    icon: ICONS.CAP_WHEELED },
      { type: 'Tracked',    factor: factors.tracked_factor,    icon: ICONS.CAP_TRACKED },
      { type: 'Helicopter', factor: factors.helicopter_factor, icon: ICONS.CAP_HELICOPTER },
      { type: 'Stretcher',  factor: factors.stretcher_factor,  icon: ICONS.CAP_STRETCHER }
    ]
  };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get evacuation asset factors for a specific unit type
 * @param {string} unitType - Unit type
 * @returns {Object} Evacuation asset factors
 */
/**
 * Get evacuation asset factors for a unit type (UI-safe, falls back to MECHANIZED).
 * Applies UNIT_TYPE_TO_EVAC_KEY adapter before lookup.
 * Use getEvacuationFactorForBrigade() in calculation engine — it throws on unknown types.
 * @param {string} unitType - Unit type key
 * @returns {Object} Evacuation asset factors
 */
export const getEvacuationFactors = (unitType) => {
  const resolvedKey = UNIT_TYPE_TO_EVAC_KEY[unitType] || unitType;
  return EVACUATION_ASSET_FACTORS[resolvedKey] || EVACUATION_ASSET_FACTORS.MECHANIZED;
};

/**
 * Get evacuation asset factors for a brigade — for use in calculation engine only.
 * Applies UNIT_TYPE_TO_EVAC_KEY adapter then throws if type still not found.
 * APP_RULES 6.1: required data must throw, not silently fallback.
 *
 * @param {string} unitType - Brigade unit_type from brigadeConfigs
 * @returns {Object} Evacuation asset factors
 * @throws {Error} If unitType not found after adapter resolution
 */
export const getEvacuationFactorForBrigade = (unitType) => {
  if (!unitType) {
    throw new Error(
      'evacuationAssetFactors.getEvacuationFactorForBrigade: ' +
      'unitType is null/undefined. brigade.unit_type must be set. ' +
      'Path: brigade.unit_type'
    );
  }
  const resolvedKey = UNIT_TYPE_TO_EVAC_KEY[unitType] || unitType;
  const factors = EVACUATION_ASSET_FACTORS[resolvedKey];
  if (!factors) {
    throw new Error(
      `evacuationAssetFactors.getEvacuationFactorForBrigade: ` +
      `unitType '${unitType}' (resolved to '${resolvedKey}') not found in EVACUATION_ASSET_FACTORS. ` +
      `Valid types: ${Object.keys(EVACUATION_ASSET_FACTORS).join(', ')}. ` +
      `Path: EVACUATION_ASSET_FACTORS['${resolvedKey}']`
    );
  }
  return factors;
};

/**
 * Calculate evacuation assets for a unit
 * @param {string} unitType - Unit type
 * @param {string} echelon - Echelon
 * @param {Object} baseAllocations - Base allocation table (optional)
 * @returns {Object} Calculated evacuation assets
 */
export const calculateEvacuationAssets = (unitType, echelon, baseAllocations = BASE_EVACUATION_ALLOCATIONS) => {
  const factors = getEvacuationFactors(unitType);
  const base = baseAllocations[echelon];
  
  if (!base) {
    throw new Error(
      `evacuationAssetFactors.calculateEvacuationAssets: ` +
      `echelon '${echelon}' not found in BASE_EVACUATION_ALLOCATIONS. ` +
      `Valid values: ${Object.keys(BASE_EVACUATION_ALLOCATIONS).join(', ')}. ` +
      `Path: BASE_EVACUATION_ALLOCATIONS[echelon]`
    );
  }
  
  const result = {
    wheeled_ambulances: Math.round(base.wheeled_ambulances * factors.wheeled_factor),
    tracked_ambulances: Math.round(base.tracked_ambulances * factors.tracked_factor),
    helicopters: Math.round(base.helicopters * factors.helicopter_factor),
    stretcher_teams: Math.round(base.stretcher_teams * factors.stretcher_factor),
    factors: {
      wheeled: factors.wheeled_factor,
      tracked: factors.tracked_factor,
      helicopter: factors.helicopter_factor,
      stretcher: factors.stretcher_factor
    },
    unit_type: factors.label,
    echelon: echelon,
    icon: factors.icon
  };
  
  return result;
};

/**
 * Get all unit types as array
 * @returns {Array} Array of unit type objects
 */
export const getUnitTypesArray = () => {
  return Object.values(EVACUATION_ASSET_FACTORS).map(factor => ({
    value: factor.unit_type,
    label: factor.label,
    description: factor.description,
    icon: factor.icon
  }));
};

/**
 * Get unit type label
 * @param {string} unitType - Unit type value
 * @returns {string} Human-readable label
 */
export const getUnitTypeLabel = (unitType) => {
  const factor = EVACUATION_ASSET_FACTORS[unitType];
  return factor ? factor.label : unitType;
};

/**
 * Get evacuation asset summary for display
 * @param {string} unitType - Unit type
 * @param {string} echelon - Echelon
 * @returns {string} Human-readable summary
 */
export const getEvacuationAssetSummary = (unitType, echelon) => {
  const assets = calculateEvacuationAssets(unitType, echelon);
  return `${ICONS.CAP_WHEELED} ${assets.wheeled_ambulances} wheeled, ${ICONS.CAP_TRACKED} ${assets.tracked_ambulances} tracked, ${ICONS.CAP_HELICOPTER} ${assets.helicopters} helicopters, ${ICONS.CAP_STRETCHER} ${assets.stretcher_teams} stretcher teams`;
};

/**
 * Calculate total ground evacuation capacity
 * @param {string} unitType - Unit type
 * @param {string} echelon - Echelon
 * @param {number} patientsPerAmbulance - Patients per ambulance (default 4)
 * @returns {Object} Ground evacuation capacity
 */
export const calculateGroundCapacity = (unitType, echelon, patientsPerAmbulance = 4) => {
  const assets = calculateEvacuationAssets(unitType, echelon);
  
  const wheeledCapacity = assets.wheeled_ambulances * patientsPerAmbulance;
  const trackedCapacity = assets.tracked_ambulances * patientsPerAmbulance;
  const stretcherCapacity = assets.stretcher_teams * 1; // 1 patient per team at a time
  
  return {
    wheeled_capacity_per_trip: wheeledCapacity,
    tracked_capacity_per_trip: trackedCapacity,
    stretcher_capacity_per_trip: stretcherCapacity,
    total_ground_capacity_per_trip: wheeledCapacity + trackedCapacity + stretcherCapacity,
    icon: ICONS.AMBULANCE
  };
};

/**
 * Get helicopter priority for unit type
 * @param {string} unitType - Unit type
 * @returns {Object} Helicopter priority info
 */
export const getHelicopterPriority = (unitType) => {
  const factor = getEvacuationFactors(unitType);
  
  let priority = 'STANDARD';
  let icon = ICONS.GREEN;
  
  if (factor.helicopter_factor >= 2.5) {
    priority = 'CRITICAL';
    icon = ICONS.RED;
  } else if (factor.helicopter_factor >= 1.5) {
    priority = 'HIGH';
    icon = ICONS.ORANGE;
  } else if (factor.helicopter_factor >= 1.0) {
    priority = 'STANDARD';
    icon = ICONS.YELLOW;
  } else {
    priority = 'LOW';
    icon = ICONS.GREEN;
  }
  
  return {
    unit_type: unitType,
    factor: factor.helicopter_factor,
    priority,
    icon,
    note: factor.rationale
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate evacuation assets calculation
 * @param {string} unitType - Unit type
 * @param {string} echelon - Echelon
 * @returns {Object} Validation result
 */
export const validateEvacuationAssets = (unitType, echelon) => {
  const errors = [];
  const warnings = [];
  
  const factors = EVACUATION_ASSET_FACTORS[unitType];
  const base = BASE_EVACUATION_ALLOCATIONS[echelon];
  
  if (!factors) {
    errors.push({
      field: 'unitType',
      message: `Invalid unit type: ${unitType}`,
      icon: ICONS.RED
    });
  }
  
  if (!base) {
    errors.push({
      field: 'echelon',
      message: `Invalid echelon: ${echelon}`,
      icon: ICONS.RED
    });
  }
  
  if (factors && base) {
    const calculated = calculateEvacuationAssets(unitType, echelon);
    
    // Check for very low allocations
    if (calculated.wheeled_ambulances < 2 && echelon !== 'BATTALION') {
      warnings.push({
        field: 'wheeled_ambulances',
        message: 'Very low wheeled ambulance allocation',
        icon: ICONS.YELLOW
      });
    }
    
    // Check for high helicopter dependency with no organic helicopters
    if (factors.helicopter_factor > 2.0 && calculated.helicopters === 0) {
      warnings.push({
        field: 'helicopters',
        message: `${factors.label} has high helicopter dependency but no organic helicopters - ensure Tab 3 allocation`,
        icon: ICONS.WARNING
      });
    }
  }
  
  return {
    valid: errors.length === 0,
    unitType: factors?.label || unitType,
    echelon: echelon,
    base: base,
    factors: factors,
    calculated: factors && base ? calculateEvacuationAssets(unitType, echelon) : null,
    summary: factors && base ? getEvacuationAssetSummary(unitType, echelon) : null,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if unit type is valid
 * @param {string} unitType - Unit type
 * @returns {boolean} True if valid
 */
export const isValidUnitType = (unitType) => {
  return unitType in EVACUATION_ASSET_FACTORS;
};

/**
 * Check if echelon is valid
 * @param {string} echelon - Echelon
 * @returns {boolean} True if valid
 */
export const isValidEchelon = (echelon) => {
  return echelon in BASE_EVACUATION_ALLOCATIONS;
};

/**
 * Get all valid unit types
 * @returns {Array} Valid unit types
 */
export const getValidUnitTypes = () => {
  return Object.keys(EVACUATION_ASSET_FACTORS);
};

/**
 * Get all valid echelons
 * @returns {Array} Valid echelons
 */
export const getValidEchelons = () => {
  return Object.keys(BASE_EVACUATION_ALLOCATIONS);
};

// ============================================================================
// CC-006: CASEVAC CONFIGURATION
// ============================================================================

/**
 * CASEVAC (Casualty Evacuation) Configuration
 * 
 * CASEVAC uses non-dedicated assets (combat vehicles, utility helicopters)
 * when MEDEVAC (dedicated medical evacuation) is unavailable or overwhelmed.
 * 
 * Doctrinal Reference:
 * - ATP 4-02.2: Medical Evacuation
 * - Ukraine 2022-2024: Heavy reliance on CASEVAC due to AD threat
 */
export const CASEVAC_CONFIGURATION = {
  // Availability rates by condition
  AVAILABILITY: {
    ON_REQUEST: {
      code: 'ON_REQUEST',
      label: 'On Request',
      availability_percent: 80,
      description: 'CASEVAC available when requested, not pre-positioned',
      response_time_min: 30,
      typical_use: 'Standard operations with available combat vehicles'
    },
    DEDICATED: {
      code: 'DEDICATED',
      label: 'Dedicated CASEVAC',
      availability_percent: 95,
      description: 'Combat vehicles pre-designated for CASEVAC mission',
      response_time_min: 15,
      typical_use: 'High-intensity ops with anticipated MEDEVAC shortfall'
    },
    LIMITED: {
      code: 'LIMITED',
      label: 'Limited Availability',
      availability_percent: 50,
      description: 'CASEVAC assets committed to combat operations',
      response_time_min: 60,
      typical_use: 'Active engagement, vehicles needed for maneuver'
    },
    UNAVAILABLE: {
      code: 'UNAVAILABLE',
      label: 'Unavailable',
      availability_percent: 10,
      description: 'Combat situation precludes CASEVAC use',
      response_time_min: 120,
      typical_use: 'Heavy contact, all assets in fight'
    }
  },
  
  // Vehicle types usable for CASEVAC
  VEHICLE_TYPES: {
    APC: {
      code: 'APC',
      label: 'Armored Personnel Carrier',
      patients_per_trip: 6,
      protection_level: 'HIGH',
      medical_capability: 'NONE',
      examples: 'M113, BTR, Stryker, Piranha'
    },
    IFV: {
      code: 'IFV',
      label: 'Infantry Fighting Vehicle',
      patients_per_trip: 4,
      protection_level: 'HIGH',
      medical_capability: 'NONE',
      examples: 'Bradley, BMP, CV90'
    },
    MRAP: {
      code: 'MRAP',
      label: 'MRAP/Protected Vehicle',
      patients_per_trip: 4,
      protection_level: 'MEDIUM',
      medical_capability: 'NONE',
      examples: 'MaxxPro, Cougar, Bushmaster'
    },
    TRUCK: {
      code: 'TRUCK',
      label: 'Cargo Truck',
      patients_per_trip: 8,
      protection_level: 'NONE',
      medical_capability: 'NONE',
      examples: 'HEMTT, M35, civilian trucks'
    },
    UTILITY_HELO: {
      code: 'UTILITY_HELO',
      label: 'Utility Helicopter',
      patients_per_trip: 6,
      protection_level: 'LOW',
      medical_capability: 'BASIC',
      examples: 'UH-60 (non-medical), Mi-8, CH-47'
    }
  }
};

/**
 * DOW (Died of Wounds) Penalties for CASEVAC vs MEDEVAC
 * 
 * CASEVAC lacks:
 * - Medical attendant during transport
 * - Medical equipment and supplies
 * - Optimized patient positioning
 * - En-route care capability
 * 
 * Ukraine 2022-2024 data shows significant DOW increase with CASEVAC
 */
export const CASEVAC_DOW_PENALTIES = {
  GROUND_CASEVAC: {
    code: 'GROUND_CASEVAC',
    label: 'Ground CASEVAC',
    dow_multiplier: 1.35,        // 35% higher DOW rate
    description: 'No en-route care, rough ride, delayed treatment',
    mitigation: 'CLS-trained crew member can reduce to 1.20x'
  },
  AIR_CASEVAC: {
    code: 'AIR_CASEVAC',
    label: 'Air CASEVAC (Utility Helo)',
    dow_multiplier: 1.15,        // 15% higher DOW rate
    description: 'Faster but no medical crew, limited equipment',
    mitigation: 'Flight medic can reduce to 1.05x'
  },
  MEDEVAC_BASELINE: {
    code: 'MEDEVAC',
    label: 'Dedicated MEDEVAC',
    dow_multiplier: 1.00,        // Baseline
    description: 'Full medical crew and equipment',
    mitigation: 'N/A - this is the standard'
  },
  PROLONGED_CASEVAC: {
    code: 'PROLONGED_CASEVAC',
    label: 'Prolonged CASEVAC (>60 min)',
    dow_multiplier: 1.60,        // 60% higher DOW rate
    description: 'Extended transport without care dramatically increases mortality',
    mitigation: 'Impossible to fully mitigate - reduce transport time'
  }
};

/**
 * AUTO Method Decision Thresholds
 * 
 * Used by calculateMethodBasedAssets() to determine area support vs direct support
 * (NATO terminology: AJP-4.10C) when evacuation method is set to AUTO
 */
export const AUTO_METHOD_THRESHOLDS = {
  // Switch to direct support (forward push) when receiving facility load exceeds this percentage
  RECEIVING_OVERLOAD_PERCENT: 80,
  
  // Switch to direct support when collection queue exceeds this many casualties
  COLLECTION_QUEUE_THRESHOLD: 20,
  
  // Minimum casualties per hour to consider direct support method
  MIN_CASUALTY_RATE_FOR_PUSH: 5,
  
  // Distance threshold - prefer direct support (forward delivery) for longer distances (km)
  DISTANCE_PUSH_PREFERRED_KM: 30,
  
  // Score threshold for AUTO decision
  SCORE_THRESHOLD_FOR_PUSH: 2,
  
  // Decision confidence levels
  CONFIDENCE: {
    HIGH: { min_score: 3, label: 'High Confidence' },
    MEDIUM: { min_score: 2, label: 'Medium Confidence' },
    LOW: { min_score: 1, label: 'Low Confidence' }
  }
};

/**
 * Direct Support Distance-Based Asset Split Ratios
 * (NATO: AJP-4.10C area support vs direct support; constant names preserved for API stability)
 *
 * When using direct support method, assets are split between collection and forward delivery.
 * Longer distances require more assets for the forward leg.
 */
export const PUSH_DISTANCE_RATIOS = {
  SHORT: {
    max_km: 10,
    collection_ratio: 0.60,
    forward_ratio: 0.40,
    description: 'Short distance - more assets for collection'
  },
  MEDIUM: {
    max_km: 25,
    collection_ratio: 0.50,
    forward_ratio: 0.50,
    description: 'Medium distance - equal split'
  },
  LONG: {
    max_km: 50,
    collection_ratio: 0.40,
    forward_ratio: 0.60,
    description: 'Long distance - more assets for forward direct support'
  },
  EXTENDED: {
    max_km: Infinity,
    collection_ratio: 0.30,
    forward_ratio: 0.70,
    description: 'Extended distance - majority for forward direct support'
  }
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named default export (fixes lint warning)
const evacuationAssetFactors = {
  BASE_EVACUATION_ALLOCATIONS,
  EVACUATION_ASSET_FACTORS,
  UKRAINE_EVACUATION_LESSONS,

  // IMP-007: Adapter map + strict factory function for calculator use
  UNIT_TYPE_TO_EVAC_KEY,

  // CC-006: CASEVAC Configuration
  CASEVAC_CONFIGURATION,
  CASEVAC_DOW_PENALTIES,
  AUTO_METHOD_THRESHOLDS,
  PUSH_DISTANCE_RATIOS,

  // UI Options
  getUnitTypeOptions,
  getEchelonOptions,
  getFactorsSummary,

  // Helper functions
  getEvacuationFactors,
  getEvacuationFactorForBrigade,
  calculateEvacuationAssets,
  getUnitTypesArray,
  getUnitTypeLabel,
  getEvacuationAssetSummary,
  calculateGroundCapacity,
  getHelicopterPriority,

  // Validation
  validateEvacuationAssets,
  isValidUnitType,
  isValidEchelon,
  getValidUnitTypes,
  getValidEchelons
};

export default evacuationAssetFactors;
